/**
 * AI Orientation Observatory (AIOO) - Main Application
 * Modular architecture with extracted layout components
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Menu, AlertCircle, CheckCircle } from "lucide-react";

import { API } from "./lib/constants";
import { Modal } from "./components/common";
import { ShareCitePanel } from "./components/share/ShareCitePanel";
import { GenderGapInfographic } from "./components/insights/GenderGapInfographic";
import { GalaxyNav } from "./components/galaxy/GalaxyNav";
import { ResearchTicker } from "./components/common/ResearchTicker";
import { GuidedTour } from "./components/tour/GuidedTour";
import { trackEvent } from "./lib/track";
import { navGroupsByGalaxy } from "./lib/navigation";

// Layout components
import { Sidebar } from "./components/layout/Sidebar";
import { AppHeader } from "./components/layout/AppHeader";
import { ContentRouter } from "./components/layout/ContentRouter";

// Form components (used in modals)
import { SubmitAppForm } from "./components/contribute/SubmitAppForm";
import { CorrectionForm } from "./components/contribute/CorrectionForm";

import { Toaster as SonnerToaster } from "sonner";
import LandingPage from "./components/LandingPage";
import { GlobalProgressBar } from "./components/common/GlobalProgressBar";
import { DataPrefetchProvider, usePrefetchedData } from "./components/common/DataPrefetch";
import { AuthProvider } from "./components/auth/AuthContext";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import PitchCard from "./components/card/PitchCard";

function App() {
  // Skip landing when returning from Google auth (session_id fragment) — AuthProvider handles the exchange
  const [showLanding, setShowLanding] = useState(() => !sessionStorage.getItem("aioo_entered") && !window.location.hash.includes("session_id="));
  const [hashRoute, setHashRoute] = useState(() => window.location.hash);

  useEffect(() => {
    const onHashChange = () => setHashRoute(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  const [showTour, setShowTour] = useState(false);
  const [tourPulse, setTourPulse] = useState(null);
  const [activeGalaxy, setActiveGalaxy] = useState("apps");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window === "undefined" ? true : window.innerWidth >= 768));
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [researchStats, setResearchStats] = useState(null);
  const [companyDetailDomain, setCompanyDetailDomain] = useState(null);
  const [companyCompareDomains, setCompanyCompareDomains] = useState([]);
  const [filters, setFilters] = useState({ value_orientation: "", founder_composition: "", country: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showCiteModal, setShowCiteModal] = useState(false);
  const [showInfographicModal, setShowInfographicModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [notification, setNotification] = useState(null);
  const prefetched = usePrefetchedData();

  // Sync researchStats from prefetch
  useEffect(() => {
    if (prefetched.researchStats && !researchStats) {
      setResearchStats(prefetched.researchStats);
    }
  });

  const fetchResearchStats = async () => {
    try {
      const res = await axios.get(`${API}/research/stats`);
      setResearchStats(res.data);
    } catch (error) {
      void 0;
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Listen for CTA navigation events
  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail);
    window.addEventListener('navigate', handler);
    window.addEventListener('navigate-tab', handler);
    return () => { window.removeEventListener('navigate', handler); window.removeEventListener('navigate-tab', handler); };
  }, []);

  // Reset tab on galaxy change
  useEffect(() => {
    if (activeGalaxy === "consciousness") setActiveTab("c-dashboard");
    else if (activeGalaxy === "hardware") setActiveTab("hw-dashboard");
    else if (activeGalaxy === "research") setActiveTab("r-dashboard");
    else if (activeGalaxy === "companies") { setActiveTab("co-dashboard"); setCompanyDetailDomain(null); setCompanyCompareDomains([]); }
    else if (activeGalaxy === "policy") setActiveTab("po-dashboard");
    else setActiveTab("dashboard");
  }, [activeGalaxy]);

  // Progressive data loading
  const fetchData = useCallback(async () => {
    // Don't fire any data calls while the landing page is showing — bootstrap
    // covers the universal data and dashboard-specific calls happen post-Enter.
    if (showLanding) return;
    if (activeGalaxy !== "apps") {
      if (!stats) {
        const quickRes = await axios.get(`${API}/stats/quick`).catch(() => null);
        if (quickRes?.data) setStats(prev => prev || { ...quickRes.data, country_distribution: {}, governance_distribution: {}, monetization_distribution: {}, source_distribution: {}, cross_tabulation: [], monthly_trend: [], monetization_by_orientation: [], lgbtq_stats: { total: 0, care_oriented: 0, care_percent: 0 }, lgbtq_focus_stats: { total: 0, care_oriented: 0, care_percent: 0, top_countries: [] } });
      }
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const quickRes = await axios.get(`${API}/stats/quick`).catch(() => null);
      if (quickRes?.data) {
        setStats(prev => prev ? { ...prev, ...quickRes.data } : {
          total_apps: quickRes.data.total_apps,
          num_countries: quickRes.data.num_countries,
          orientation_distribution: quickRes.data.orientation_distribution,
          founder_distribution: quickRes.data.founder_distribution,
          country_distribution: {}, governance_distribution: {}, monetization_distribution: {},
          source_distribution: {}, cross_tabulation: [], monthly_trend: [],
          monetization_by_orientation: [],
          lgbtq_stats: { total: 0, care_oriented: 0, care_percent: 0 },
          lgbtq_focus_stats: { total: 0, care_oriented: 0, care_percent: 0, top_countries: [] },
        });
        setLoading(false);
      }
      const params = { ...filters, limit: 200 };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const [statsRes, appsRes, researchRes] = await Promise.allSettled([
        axios.get(`${API}/stats`),
        axios.get(`${API}/apps`, { params }),
        axios.get(`${API}/research/stats`)
      ]);
      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      if (appsRes.status === "fulfilled") setApps(appsRes.value.data);
      if (researchRes.status === "fulfilled") setResearchStats(researchRes.value.data);
    } catch (error) {
      void 0;
      showNotification("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, activeGalaxy, showLanding, stats]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const seedData = async () => {
    try {
      const res = await axios.post(`${API}/seed`);
      showNotification(res.data.message, "success");
      fetchData();
    } catch (error) {
      showNotification("Error seeding data", "error");
    }
  };

  const generateInsights = async (query = "") => {
    setInsightsLoading(true);
    try {
      const res = await axios.post(`${API}/insights`, { query });
      setInsights(res.data);
    } catch (error) {
      void 0;
      showNotification("Error generating insights", "error");
    } finally {
      setInsightsLoading(false);
    }
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const filteredApps = apps;
  const navGroups = navGroupsByGalaxy[activeGalaxy] || navGroupsByGalaxy.apps;

  const handleEnterObservatory = () => {
    sessionStorage.setItem("aioo_entered", "1");
    setShowLanding(false);
    setActiveGalaxy("consciousness");
    setActiveTab("c-dashboard");
    trackEvent("enter_observatory", "landing");
    if (!localStorage.getItem("aioo_tour_done")) {
      // Tour pulse comes from the bootstrap prefetch — no separate fetch needed.
      setShowTour(true);
    }
  };

  // Hash-route: open AdminDashboard at <site>#admin — bypasses landing & sidebar
  if (hashRoute === "#admin") {
    return (<>
      <SonnerToaster position="top-right" theme="dark" />
      <AdminDashboard />
    </>);
  }

  // Hash-route: public pitch card at <site>#card — QR-code landing for in-person shares
  if (hashRoute === "#card") {
    return <PitchCard />;
  }

  if (showLanding) {
    return (
      <DataPrefetchProvider>
        <GlobalProgressBar />
        <LandingPage onEnter={handleEnterObservatory} />
        <SonnerToaster position="bottom-center" theme="dark" toastOptions={{ style: { background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", color: "#e2e8f0" } }} />
      </DataPrefetchProvider>
    );
  }

  return (
    <DataPrefetchProvider>
    <div className="min-h-screen flex">
      <GlobalProgressBar />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}
      {!sidebarOpen && (
        <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"
          className="fixed top-3 left-3 z-50 md:hidden p-2 bg-card border border-border rounded-lg shadow-lg">
          <Menu className="w-5 h-5" />
        </button>
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navGroups={navGroups}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        activeGalaxy={activeGalaxy}
        setActiveGalaxy={setActiveGalaxy}
        setShowLanding={setShowLanding}
      />

      <main className={`flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0 md:ml-16'}`}>
        <GalaxyNav activeGalaxy={activeGalaxy} setActiveGalaxy={setActiveGalaxy} />

        <AppHeader
          loading={loading}
          fetchData={fetchData}
          setActiveTab={setActiveTab}
          setShowLanding={setShowLanding}
          setShowCiteModal={setShowCiteModal}
        />

        <ResearchTicker />

        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-2 flex items-center gap-2 text-xs text-muted-foreground border-b border-border/30" data-testid="breadcrumb">
          <button onClick={() => { setActiveGalaxy("consciousness"); setActiveTab("c-dashboard"); }} className="hover:text-foreground transition-colors">Universe</button>
          {activeGalaxy !== "consciousness" && (
            <>
              <span className="text-muted-foreground/30">/</span>
              <button onClick={() => setActiveTab(activeGalaxy === "apps" ? "dashboard" : activeGalaxy === "hardware" ? "hw-dashboard" : activeGalaxy === "research" ? "r-dashboard" : activeGalaxy === "companies" ? "co-dashboard" : "po-dashboard")} className="hover:text-foreground transition-colors">
                {activeGalaxy === "apps" ? "AI Apps" : activeGalaxy === "hardware" ? "Physical AI" : activeGalaxy === "research" ? "Research" : activeGalaxy === "companies" ? "Companies" : activeGalaxy === "policy" ? "Policy" : activeGalaxy}
              </button>
            </>
          )}
          {activeTab && !["c-dashboard", "dashboard", "hw-dashboard", "r-dashboard", "co-dashboard", "po-dashboard"].includes(activeTab) && (
            <>
              <span className="text-muted-foreground/30">/</span>
              <span className="text-foreground font-medium">
                {navGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || activeTab}
              </span>
            </>
          )}
        </div>

        <ContentRouter
          activeGalaxy={activeGalaxy}
          activeTab={activeTab}
          stats={stats}
          researchStats={researchStats}
          apps={apps}
          filteredApps={filteredApps}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setSelectedApp={setSelectedApp}
          setShowCorrectionModal={setShowCorrectionModal}
          insights={insights}
          insightsLoading={insightsLoading}
          generateInsights={generateInsights}
          setShowInfographicModal={setShowInfographicModal}
          fetchResearchStats={fetchResearchStats}
          showNotification={showNotification}
          companyDetailDomain={companyDetailDomain}
          setCompanyDetailDomain={setCompanyDetailDomain}
          setCompanyCompareDomains={setCompanyCompareDomains}
          companyCompareDomains={companyCompareDomains}
          setActiveTab={setActiveTab}
          setActiveGalaxy={setActiveGalaxy}
        />

        <footer className="border-t border-border/50 px-6 py-4 text-center" data-testid="app-footer">
          <p className="text-xs text-muted-foreground">&copy; 2026 Priyanka Jha. All rights reserved.</p>
        </footer>
      </main>

      {/* Modals */}
      {showSubmitModal && (
        <Modal onClose={() => setShowSubmitModal(false)} title="Submit an AI App">
          <SubmitAppForm onSuccess={() => { setShowSubmitModal(false); fetchData(); showNotification("App submitted successfully!", "success"); }} compact />
        </Modal>
      )}
      {showCorrectionModal && selectedApp && (
        <Modal onClose={() => { setShowCorrectionModal(false); setSelectedApp(null); }} title="Submit Correction">
          <CorrectionForm app={selectedApp} onSuccess={() => { setShowCorrectionModal(false); setSelectedApp(null); showNotification("Correction submitted for review!", "success"); }} />
        </Modal>
      )}
      {showCiteModal && (
        <Modal onClose={() => setShowCiteModal(false)} title="Share & Cite This Dataset">
          <ShareCitePanel />
        </Modal>
      )}
      {showInfographicModal && (
        <Modal onClose={() => setShowInfographicModal(false)} title="AI Gender Gap Infographic" wide>
          <GenderGapInfographic onClose={() => setShowInfographicModal(false)} />
        </Modal>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-fade-in ${
          notification.type === "error" ? "bg-red-900/90 border border-red-700" : 
          notification.type === "success" ? "bg-green-900/90 border border-green-700" : 
          "bg-card border border-border"
        }`}>
          {notification.type === "error" ? <AlertCircle className="w-5 h-5 text-red-400" /> : 
           notification.type === "success" ? <CheckCircle className="w-5 h-5 text-green-400" /> : null}
          <span>{notification.message}</span>
        </div>
      )}
      <SonnerToaster position="bottom-center" theme="dark" toastOptions={{ style: { background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", color: "#e2e8f0" } }} />
      {showTour && (
        <GuidedTour pulse={prefetched.pulse || tourPulse} onComplete={() => {
          setShowTour(false);
          localStorage.setItem("aioo_tour_done", "1");
          trackEvent("tour_completed", "tour");
        }} />
      )}
    </div>
    </DataPrefetchProvider>
  );
}

export default function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
